import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from 'react-query';
import { updateAccessControl } from 'services/transparency-config-service';
import { DotsLoader } from 'Components/DotsLoader';
import { ChipsInput } from 'Components/common/ChipsInput';
import { Dialog } from 'Components/ui';
import styled from 'styled-components';

const Card = styled.div`
  max-width: 680px;
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`

const Title = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 4px;
`

const Subtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  margin: 0 0 24px;
  line-height: 1.6;
`

const Divider = styled.div`
  height: 1px;
  background: var(--vo-cloud);
  margin: 20px 0;
`

const Section = styled.div`
  margin-bottom: 28px;
`

const SectionTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 4px;
`

const SectionSub = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  margin: 0 0 14px;
  line-height: 1.55;
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
`

const SaveButton = styled.button`
  height: 44px;
  background: var(--color-brand);
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  &:hover { background: var(--color-brand-strong); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`

export const AccessControl = ({ config, onUpdate, companyKey }) => {
  const { t } = useTranslation(['transparency', 'common']);
  const { control, handleSubmit } = useForm({
    defaultValues: {
      allowedEmailDomains: config.allowedEmailDomains || [],
      allowedEmails: config.allowedEmails || [],
    },
  });

  const mutation = useMutation(
    (data) => updateAccessControl(companyKey, data),
    {
      onSuccess: () => {
        Dialog.alert({
          title: t('transparency:accessControl.notifications.success'),
          message: t('transparency:accessControl.notifications.successText'),
          icon: 'success',
        });
        onUpdate();
      },
      onError: (error) => {
        Dialog.alert({
          title: t('transparency:accessControl.notifications.error'),
          message: error.response?.data?.message || error.message,
          icon: 'error',
        });
      },
    }
  );

  const onSubmit = (data) => {
    mutation.mutate(data);
  };

  return (
    <Card>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Title>{t('transparency:accessControl.title')}</Title>
        <Subtitle>{t('transparency:accessControl.subtitle')}</Subtitle>

        <Divider />

        <Section>
          <SectionTitle>{t('transparency:accessControl.domains.title')}</SectionTitle>
          <SectionSub>{t('transparency:accessControl.domains.subtitle')}</SectionSub>
          <Controller
            control={control}
            name="allowedEmailDomains"
            render={({ field: { onChange, value } }) => (
              <ChipsInput
                value={value}
                onChange={onChange}
                placeholder={t('transparency:accessControl.domains.placeholder')}
              />
            )}
          />
        </Section>

        <Section>
          <SectionTitle>{t('transparency:accessControl.emails.title')}</SectionTitle>
          <SectionSub>{t('transparency:accessControl.emails.subtitle')}</SectionSub>
          <Controller
            control={control}
            name="allowedEmails"
            render={({ field: { onChange, value } }) => (
              <ChipsInput
                value={value}
                onChange={onChange}
                placeholder={t('transparency:accessControl.emails.placeholder')}
              />
            )}
          />
        </Section>

        <Divider />

        <Actions>
          <SaveButton type="submit" disabled={mutation.isLoading}>
            {mutation.isLoading ? <DotsLoader /> : t('common:buttons.save')}
          </SaveButton>
        </Actions>
      </form>
    </Card>
  );
};
